/**
 * Language codes used across the shop.
 *
 * Internally the app works with ISO 639-1 codes only (`i18n.language`,
 * localStorage "currentLanguage", Intl formatting, i18n-iso-countries). Other
 * code systems are translated at the boundary to the system that uses them:
 * - `legacyCode`: the customer's codes as used by the legacy webshop backend
 *   (`/api/language/list`, `PUT /api/language`); some aren't ISO ("cz", "si", "se")
 * - `cpqLocale`: locale id of the Plansoft backends (UI dictionary,
 *   configuration service)
 * - `countryCode`: ISO 3166-1 alpha-2 country for flags and address defaults
 */
export interface Language {
  code: string;
  legacyCode: string;
  cpqLocale: string;
  countryCode: string;
  /** native name, as shown in the language select */
  name: string;
}

export const DEFAULT_LANGUAGE = "de";

export const LANGUAGES: Language[] = [
  {
    code: "de",
    legacyCode: "de",
    cpqLocale: "de-DE",
    countryCode: "DE",
    name: "Deutsch",
  },
  {
    code: "en",
    legacyCode: "en",
    cpqLocale: "en-US",
    countryCode: "GB",
    name: "English",
  },
  {
    code: "cs",
    legacyCode: "cz",
    cpqLocale: "cs-CZ",
    countryCode: "CZ",
    name: "čeština",
  },
  {
    code: "es",
    legacyCode: "es",
    cpqLocale: "es-ES",
    countryCode: "ES",
    name: "Español",
  },
  {
    code: "fr",
    legacyCode: "fr",
    cpqLocale: "fr-FR",
    countryCode: "FR",
    name: "Français",
  },
  {
    code: "hu",
    legacyCode: "hu",
    cpqLocale: "hu-HU",
    countryCode: "HU",
    name: "Magyar",
  },
  {
    code: "it",
    legacyCode: "it",
    cpqLocale: "it-IT",
    countryCode: "IT",
    name: "italiano",
  },
  {
    code: "nl",
    legacyCode: "nl",
    cpqLocale: "nl-NL",
    countryCode: "NL",
    name: "Nederlands",
  },
  {
    code: "pl",
    legacyCode: "pl",
    cpqLocale: "pl-PL",
    countryCode: "PL",
    name: "Polski",
  },
  {
    code: "pt",
    legacyCode: "pt",
    cpqLocale: "pt-PT",
    countryCode: "PT",
    name: "Português",
  },
  {
    code: "ro",
    legacyCode: "ro",
    cpqLocale: "ro-RO",
    countryCode: "RO",
    name: "Română",
  },
  {
    code: "sv",
    legacyCode: "se",
    // the UI dictionary keys Swedish as "se-SE"; the configuration service
    // has no Swedish locale
    cpqLocale: "se-SE",
    countryCode: "SE",
    name: "Svenska",
  },
  {
    code: "sl",
    legacyCode: "si",
    cpqLocale: "sl-SI",
    countryCode: "SI",
    name: "Slovenščina",
  },
];

function findLanguage(code: string): Language | undefined {
  return LANGUAGES.find((language) => language.code === code);
}

/** Maps a legacy backend code to ISO; unknown codes pass through unchanged. */
export function fromLegacyCode(legacyCode: string): string {
  return (
    LANGUAGES.find((language) => language.legacyCode === legacyCode)?.code ??
    legacyCode
  );
}

/** Maps an ISO code to the legacy backend code; unknown codes pass through. */
export function toLegacyCode(code: string): string {
  return findLanguage(code)?.legacyCode ?? code;
}

/** Maps an ISO code to the Plansoft locale id; unknown codes pass through. */
export function toCpqLocale(code: string): string {
  return findLanguage(code)?.cpqLocale ?? code;
}

/** ISO 3166-1 alpha-2 country for an ISO language code. */
export function getCountryCode(code: string): string {
  return findLanguage(code)?.countryCode ?? code.toUpperCase();
}

export function getLanguageName(code: string): string | undefined {
  return findLanguage(code)?.name;
}
